import { SITE_URL, DEFAULT_OG_IMAGE } from "@/lib/seo";

/**
 * Canonical leaf-route head helper.
 *
 * Every shareable leaf route should emit a self-referencing
 * `<link rel="canonical">` + matching `og:url` so crawlers attribute
 * the page's title / description / image to THIS URL — never the
 * homepage or a parent. Use this helper anywhere a route doesn't
 * already flow through `buildAliasHead`, `cityHeadMeta`,
 * `countryHeadMeta`, or `buildBillHead`.
 *
 * Usage:
 *   head: () => leafHead({
 *     path: "/tools/gold-purity-converter",
 *     title: "...",
 *     description: "...",
 *   }),
 */
export interface LeafHeadArgs {
  path: string; // must start with "/"
  title: string;
  description: string;
  ogImage?: string;
  ogType?: "website" | "article" | "product";
  locale?: string;
  noindex?: boolean;
  extraMeta?: Array<Record<string, string>>;
  extraLinks?: Array<Record<string, string>>;
  scripts?: Array<{ type: string; children: string } | Record<string, unknown>>;
}

export function leafHead(args: LeafHeadArgs) {
  const pageUrl = `${SITE_URL}${args.path}`;
  const ogImage = args.ogImage ?? DEFAULT_OG_IMAGE;
  const ogType = args.ogType ?? "website";

  const meta: Array<Record<string, string>> = [
    { title: args.title },
    { name: "description", content: args.description },
    // Reassert Discover eligibility at leaf level — survives any
    // future root override.
    {
      name: "robots",
      content: args.noindex
        ? "noindex, follow"
        : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
    },
    { property: "og:title", content: args.title },
    { property: "og:description", content: args.description },
    { property: "og:url", content: pageUrl },
    { property: "og:type", content: ogType },
    { property: "og:image", content: ogImage },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: args.title },
    { name: "twitter:description", content: args.description },
    { name: "twitter:image", content: ogImage },
  ];
  if (args.locale) meta.push({ property: "og:locale", content: args.locale });
  if (args.extraMeta) meta.push(...args.extraMeta);

  const links: Array<Record<string, string>> = [
    { rel: "canonical", href: pageUrl },
  ];
  if (args.extraLinks) links.push(...args.extraLinks);

  return {
    meta,
    links,
    ...(args.scripts ? { scripts: args.scripts } : {}),
  };
}